import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, type ReactElement } from 'react'

import {
  AssignDialog,
  ReplyComposer,
  StatusUpdateDialog,
  TicketDetailLayout,
  supportTicketDetailQuery,
  useAddTicketMessage,
  useAssignTicket,
  useSetTicketStatus,
} from '~/features/support'
import { UserDetailsSheet } from '~/features/users/components/UserDetailsSheet'
import { logger } from '~/lib/logger'

/**
 * `/admin/support/$ticketId` — single-ticket detail page.
 *
 * Composes the canonical `TicketDetailLayout` with the `ReplyComposer`
 * and the Assign / Status dialogs. All mutations live in
 * `~/features/support` and are wired here so the layout stays purely
 * presentational.
 *
 * UserDetailsSheet integration: the canonical sheet from
 * `~/features/users/components/UserDetailsSheet` is being built by a
 * parallel agent. Until it lands, "Open user details" is wired to a
 * TODO that logs an info-level event so we don't block the rest of the
 * surface. When the sheet lands, replace the handler with the import +
 * sheet state.
 */
export const Route = createFileRoute('/admin/support/$ticketId')({
  component: TicketDetailRoute,
})

function TicketDetailRoute(): ReactElement {
  const { ticketId } = Route.useParams()
  const navigate = useNavigate()
  const detailQuery = useQuery(supportTicketDetailQuery(ticketId))

  const addMessage = useAddTicketMessage()
  const setStatus = useSetTicketStatus()
  const assign = useAssignTicket()

  const [assignOpen, setAssignOpen] = useState(false)
  const [statusOpen, setStatusOpen] = useState(false)
  const [userSheetOpen, setUserSheetOpen] = useState(false)

  if (detailQuery.isLoading) {
    return (
      <div className="ka-page" aria-busy="true">
        <div className="ka-card ka-page-section ka-card-body--lg">
          <div className="ka-sk ka-sk--title" />
          <div className="ka-sk ka-sk--body-sm ka-sk--mt" />
        </div>
        <div className="ka-card ka-card-body--lg">
          <div className="ka-sk ka-sk--body" />
          <div className="ka-sk ka-sk--body-md ka-sk--mt" />
          <div className="ka-sk ka-sk--body ka-sk--mt" />
        </div>
      </div>
    )
  }

  if (detailQuery.isError || !detailQuery.data) {
    const message =
      detailQuery.error instanceof Error
        ? detailQuery.error.message
        : 'Could not load ticket.'
    return (
      <div className="ka-page">
        <div role="alert" className="ka-card ka-card--error">
          <div className="ka-empty">
            <div className="ka-empty-title">Couldn't load ticket</div>
            <div className="ka-empty-body">{message}</div>
          </div>
        </div>
      </div>
    )
  }

  const detail = detailQuery.data

  const handleSendReply = ({
    message,
    isInternal,
  }: {
    message: string
    isInternal: boolean
  }): void => {
    addMessage.mutate(
      { ticketId, message, isInternal },
      {
        onError: (cause) =>
          logger.warn('support: send reply failed', { ticketId, cause }),
      },
    )
  }

  // The ticket schema is `passthrough()`, so a future backend enrichment
  // (Section B2 of the rollup plan) that adds `customer_user_id` will be
  // picked up here without further client changes. Until then this is
  // `null` for tickets created via the unauthenticated form.
  const customerUserId =
    (detail.ticket as { customer_user_id?: string | null }).customer_user_id ??
    null
  const handleOpenUserDetails = (): void => {
    if (customerUserId === null) {
      logger.info('support: open user details skipped (no customer_user_id)', {
        ticketId,
      })
      return
    }
    setUserSheetOpen(true)
  }

  return (
    <>
      <TicketDetailLayout
        detail={detail}
        onBack={() =>
          navigate({
            to: '/admin/support',
            search: { view: 'table', page: 1, limit: 25 },
          })
        }
        onAssignClick={() => setAssignOpen(true)}
        onStatusClick={() => setStatusOpen(true)}
        onOpenUserDetails={handleOpenUserDetails}
        replyComposer={
          <ReplyComposer
            ticketId={ticketId}
            isSending={addMessage.isPending}
            errorMessage={
              addMessage.isError && addMessage.error instanceof Error
                ? addMessage.error.message
                : null
            }
            onSend={handleSendReply}
          />
        }
      />

      <AssignDialog
        open={assignOpen}
        currentAssigneeId={detail.ticket.assigned_to ?? null}
        isSubmitting={assign.isPending}
        errorMessage={
          assign.isError && assign.error instanceof Error
            ? assign.error.message
            : null
        }
        onClose={() => setAssignOpen(false)}
        onAssign={(adminUserId) => {
          assign.mutate(
            { ticketId, adminUserId },
            { onSuccess: () => setAssignOpen(false) },
          )
        }}
      />

      <StatusUpdateDialog
        open={statusOpen}
        currentStatus={detail.ticket.status}
        isSubmitting={setStatus.isPending}
        errorMessage={
          setStatus.isError && setStatus.error instanceof Error
            ? setStatus.error.message
            : null
        }
        onClose={() => setStatusOpen(false)}
        onConfirm={(status) => {
          setStatus.mutate(
            { ticketId, status },
            { onSuccess: () => setStatusOpen(false) },
          )
        }}
      />

      {customerUserId !== null ? (
        <UserDetailsSheet
          userId={customerUserId}
          open={userSheetOpen}
          onClose={() => setUserSheetOpen(false)}
        />
      ) : null}
    </>
  )
}
